// Retkitukikohdan pienoismaailma (suunnitelman kohta 12.2): hieman
// yläviistosta kuvattu saari järvessä. Piirretään isometrisenä SVG:nä,
// jotta se skaalautuu terävänä kaikille näytöille ja toimii ilman verkkoa.
//
// Koordinaatisto: saari on ruudukko x, y ∈ [0, 8]. Funktio P muuntaa
// maailman pisteen (x, y, korkeus) ruudulle. +x näkyy oikealle alas,
// +y vasemmalle alas. Kohteet piirretään järjestyksessä x + y, jolloin
// edessä oleva peittää takana olevan oikein.

const TILE_W = 30; // puolikas ruudun leveys pikseleinä
const TILE_H = 15; // puolikas ruudun korkeus pikseleinä

const P = (x, y, z = 0) => [(x - y) * TILE_W, (x + y) * TILE_H - z];
const pts = (...points) => points.map((p) => p.join(",")).join(" ");

/** Vuodenaikojen värit. Talvella lunta, syksyllä ruskaa. */
const SEASONS = {
  talvi: { label: "Talvi", ground: "#e8eef0", groundSide: "#b9c6cc", trees: ["#2f5a46", "#3c6b55"], treeTop: "#f4f8f9", water: "#a9c6d6", roof: "#f1f5f6" },
  kevat: { label: "Kevät", ground: "#9cc77a", groundSide: "#6f8f53", trees: ["#4f8f4a", "#78b35d"], treeTop: null, water: "#4f95c4", roof: "#8a4b33" },
  kesa: { label: "Kesä", ground: "#7fb45c", groundSide: "#5b8642", trees: ["#2f6b3b", "#3e7f45"], treeTop: null, water: "#2f80b8", roof: "#8a4b33" },
  syksy: { label: "Syksy", ground: "#a7a463", groundSide: "#7d7a45", trees: ["#2f5a3c", "#d08a2e", "#c4522f", "#e2b33d"], treeTop: null, water: "#3f78a0", roof: "#7a3e2b" },
};

const WOOD = { top: "#c9a26b", left: "#9b7445", right: "#b38956" };
const RED = { top: "#b54a35", left: "#8f3626", right: "#a3402e" };

// --- Piirtoalkeet --------------------------------------------------------------

/** Laatikko: pohja (x, y), koko w × d ruutua, korkeus h pikseliä. */
function Box({ x, y, w, d, h, c, z = 0 }) {
  const A = P(x, y, z + h), B = P(x + w, y, z + h), C = P(x + w, y + d, z + h), D = P(x, y + d, z + h);
  return (
    <g>
      <polygon points={pts(P(x, y + d, z), P(x + w, y + d, z), C, D)} fill={c.left} />
      <polygon points={pts(P(x + w, y, z), P(x + w, y + d, z), C, B)} fill={c.right} />
      <polygon points={pts(A, B, C, D)} fill={c.top} />
    </g>
  );
}

/** Harjakatto x-suuntaisella harjalla. */
function GableRoof({ x, y, w, d, h, rise, color, z = 0 }) {
  const ridgeA = P(x, y + d / 2, z + h + rise), ridgeB = P(x + w, y + d / 2, z + h + rise);
  const A = P(x, y, z + h), B = P(x + w, y, z + h), C = P(x + w, y + d, z + h), D = P(x, y + d, z + h);
  return (
    <g>
      <polygon points={pts(A, B, ridgeB, ridgeA)} fill={color} opacity="0.8" />
      <polygon points={pts(B, C, ridgeB)} fill="#d8c3a0" />
      <polygon points={pts(D, C, ridgeB, ridgeA)} fill={color} />
    </g>
  );
}

function Tree({ x, y, s = 1, color, snow }) {
  const [bx, by] = P(x, y);
  const h = 34 * s, r = 11 * s;
  return (
    <g>
      <ellipse cx={bx} cy={by} rx={r * 0.8} ry={r * 0.4} fill="rgba(0,0,0,.14)" />
      <rect x={bx - 1.5} y={by - 6} width="3" height="6" fill="#6b4a2b" />
      <polygon points={pts([bx - r, by - 5], [bx + r, by - 5], [bx, by - h])} fill={color} />
      {snow && <polygon points={pts([bx - r * 0.45, by - h * 0.62], [bx + r * 0.45, by - h * 0.62], [bx, by - h])} fill={snow} />}
    </g>
  );
}

// --- Rakennukset ---------------------------------------------------------------

const BUILDINGS = {
  leiri: { x: 3.5, y: 3.4, draw: () => (
    <g>
      {(() => { const [cx, cy] = P(4, 3.9); return (
        <g>
          <ellipse cx={cx} cy={cy} rx="15" ry="7.5" fill="#6d6a63" />
          <ellipse cx={cx} cy={cy - 1} rx="10" ry="5" fill="#2d2a26" />
          <polygon class="flame" points={pts([cx - 5, cy - 1], [cx + 5, cy - 1], [cx, cy - 16])} fill="#f0a23a" />
          <polygon class="flame" points={pts([cx - 2.5, cy - 1], [cx + 2.5, cy - 1], [cx, cy - 9])} fill="#ffd66b" />
        </g>
      ); })()}
      <Box x={4.6} y={3.3} w={0.3} d={1.1} h={6} c={WOOD} />
    </g>
  ) },
  laavu: { x: 1.4, y: 4.4, draw: () => {
    const z0 = 0, back = 26;
    return (
      <g>
        <polygon points={pts(P(1.4, 4.4, z0 + back), P(2.6, 4.4, z0 + back), P(2.6, 5.4, z0), P(1.4, 5.4, z0))} fill={WOOD.top} />
        <polygon points={pts(P(2.6, 4.4, z0), P(2.6, 4.4, z0 + back), P(2.6, 5.4, z0))} fill={WOOD.right} />
        <Box x={1.6} y={5.0} w={0.8} d={0.25} h={5} c={WOOD} />
      </g>
    );
  } },
  vaja: { x: 5.0, y: 1.4, draw: () => (
    <g>
      <Box x={5.0} y={1.4} w={1.2} d={0.9} h={20} c={RED} />
      <polygon points={pts(P(5.45, 2.3, 0), P(5.75, 2.3, 0), P(5.75, 2.3, 13), P(5.45, 2.3, 13))} fill="#5d2418" />
      <GableRoof x={5.0} y={1.4} w={1.2} d={0.9} h={20} rise={12} color="var(--roof)" />
    </g>
  ) },
  karttahuone: { x: 1.8, y: 1.6, draw: () => (
    <g>
      <Box x={1.8} y={1.6} w={1.6} d={1.2} h={26} c={{ top: "#e9dfc8", left: "#c7b893", right: "#d9cba8" }} />
      {/* Ikkunat etuseinällä (y = 2.8): suunnikas seinän tasossa */}
      {[2.05, 2.75].map((x1) => (
        <polygon key={x1} points={pts(P(x1, 2.8, 10), P(x1 + 0.4, 2.8, 10), P(x1 + 0.4, 2.8, 19), P(x1, 2.8, 19))} fill="#3b6f9a" />
      ))}
      <GableRoof x={1.8} y={1.6} w={1.6} d={1.2} h={26} rise={14} color="var(--roof)" />
    </g>
  ) },
  kasvihuone: { x: 5.3, y: 4.3, draw: () => (
    <g>
      <Box x={5.3} y={4.3} w={1.3} d={0.9} h={16} c={{ top: "rgba(220,240,230,.75)", left: "rgba(160,205,190,.75)", right: "rgba(190,225,210,.75)" }} />
      <GableRoof x={5.3} y={4.3} w={1.3} d={0.9} h={16} rise={10} color="rgba(200,232,220,.85)" />
      {(() => { const [px, py] = P(5.9, 4.9, 2); return <circle cx={px} cy={py - 4} r="4" fill="#4f8f4a" />; })()}
    </g>
  ) },
  laituri: { x: 7.3, y: 6.2, draw: () => (
    <g>
      <Box x={7.4} y={6.3} w={2.0} d={0.6} h={4} c={WOOD} />
      {(() => { const [bx, by] = P(9.0, 7.3); return <path d={`M${bx - 14} ${by} q14 9 28 0 l-4 -4 h-20 z`} fill="#b5523a" />; })()}
    </g>
  ) },
  torni: { x: 0.8, y: 6.2, draw: () => (
    <g>
      {[[0.8, 6.2], [1.4, 6.2], [0.8, 6.8], [1.4, 6.8]].map(([lx, ly]) => <Box key={`${lx}${ly}`} x={lx} y={ly} w={0.12} d={0.12} h={52} c={WOOD} />)}
      <Box x={0.7} y={6.1} w={0.95} d={0.95} h={4} z={52} c={WOOD} />
      <GableRoof x={0.7} y={6.1} w={0.95} d={0.95} h={4} z={64} rise={10} color="var(--roof)" />
    </g>
  ) },
};

// --- Koristeet ---------------------------------------------------------------

const DECOR = {
  viiri: { x: 4.7, y: 2.8, draw: ([x, y]) => <g><rect x={x - 1} y={y - 40} width="2" height="40" fill="#5a5a5a" /><polygon points={pts([x + 1, y - 40], [x + 20, y - 34], [x + 1, y - 28])} fill="#b0137a" /></g> },
  polkuviitta: { x: 3.0, y: 5.6, draw: ([x, y]) => <g><rect x={x - 1.5} y={y - 22} width="3" height="22" fill="#6b4a2b" /><polygon points={pts([x - 12, y - 22], [x + 10, y - 22], [x + 15, y - 18], [x + 10, y - 14], [x - 12, y - 14])} fill="#e8d6a8" stroke="#6b4a2b" /></g> },
  kummeli: { x: 6.6, y: 2.8, draw: ([x, y]) => <g>{[[0, 0, 9], [-1, -7, 7], [1, -13, 5]].map(([dx, dy, r]) => <ellipse key={dy} cx={x + dx} cy={y + dy} rx={r} ry={r * 0.6} fill="#8d918e" stroke="#6c706d" />)}</g> },
  opastaulu: { x: 2.6, y: 3.6, draw: ([x, y]) => <g><rect x={x - 9} y={y - 20} width="2" height="20" fill="#6b4a2b" /><rect x={x + 7} y={y - 20} width="2" height="20" fill="#6b4a2b" /><rect x={x - 12} y={y - 30} width="24" height="14" fill="#2e5d43" stroke="#e8d6a8" /></g> },
  lohkare: { x: 6.7, y: 5.9, draw: ([x, y]) => <path d={`M${x - 16} ${y} q-2 -18 14 -20 q18 0 18 20 z`} fill="#9a9c98" stroke="#737571" /> },
  sulkuportti: { x: 7.4, y: 3.9, draw: ([x, y]) => <g><rect x={x - 14} y={y - 24} width="4" height="24" fill="#5b5f63" /><rect x={x + 10} y={y - 24} width="4" height="24" fill="#5b5f63" /><rect x={x - 10} y={y - 20} width="20" height="16" fill="#8a6a43" /></g> },
  pitkospuut: { x: 4.1, y: 6.6, draw: () => <g>{[0, 0.45, 0.9].map((o) => <Box key={o} x={3.6 + o} y={6.5} w={0.4} d={0.28} h={2} c={WOOD} />)}</g> },
  tykki: { x: 0.9, y: 3.0, draw: ([x, y]) => <g><circle cx={x - 6} cy={y - 4} r="5" fill="#4b3a2a" /><circle cx={x + 6} cy={y - 1} r="5" fill="#4b3a2a" /><rect x={x - 6} y={y - 14} width="22" height="7" rx="3.5" fill="#34383b" transform={`rotate(-18 ${x} ${y - 10})`} /></g> },
};

// Puut: kiinteät paikat, jotka eivät osu rakennuspaikoille.
const TREES = [[0.4, 0.6, 1.1], [0.6, 1.8, 0.9], [1.2, 0.3, 1], [3.8, 0.4, 1.2], [4.6, 0.6, 0.9], [7.2, 0.6, 1.1], [7.5, 1.7, 0.8],
  [0.3, 5.2, 1], [0.5, 7.6, 1.2], [2.5, 7.4, 0.9], [3.4, 7.6, 1], [6.9, 3.0, 0.8], [7.6, 5.2, 1.1], [5.0, 7.4, 1], [6.3, 0.2, 1]];

/** Rakennuspaikka, jolle voi rakentaa: katkoviivainen tontti ja plus-merkki. */
function Plot({ x, y, highlight }) {
  const w = 1.2, d = 1.0;
  const [cx, cy] = P(x + w / 2, y + d / 2);
  return (
    <g>
      <polygon points={pts(P(x, y), P(x + w, y), P(x + w, y + d), P(x, y + d))} fill={highlight ? "rgba(176,19,122,.18)" : "rgba(255,255,255,.18)"}
        stroke={highlight ? "#b0137a" : "rgba(255,255,255,.7)"} stroke-width="1.5" stroke-dasharray="4 3" />
      {highlight && <path d={`M${cx - 6} ${cy} h12 M${cx} ${cy - 6} v12`} stroke="#b0137a" stroke-width="2.5" stroke-linecap="round" />}
    </g>
  );
}

export function BaseScene({ state, selected, onSelect, reducedMotion }) {
  const season = SEASONS[state.season] ?? SEASONS.kesa;
  const builtIds = new Set(state.buildings.filter((b) => b.built).map((b) => b.id));
  const canBuild = state.slots_free > 0;
  const decorShown = state.decor.filter((d) => d.unlocked && d.visible);

  // Kaikki maailman esineet yhteen listaan syvyysjärjestystä varten.
  const items = [];
  TREES.forEach(([x, y, s], i) => items.push({ key: `t${i}`, depth: x + y, el: <Tree x={x} y={y} s={s} color={season.trees[i % season.trees.length]} snow={season.treeTop} /> }));
  for (const b of state.buildings) {
    const spot = BUILDINGS[b.id];
    if (!spot) continue;
    const isSelected = selected === b.id;
    const body = b.built ? spot.draw() : <Plot x={spot.x} y={spot.y} highlight={canBuild || isSelected} />;
    items.push({
      key: b.id,
      depth: spot.x + spot.y + 1,
      el: (
        <g
          class={`spot ${isSelected ? "selected" : ""}`}
          role="button"
          tabIndex={0}
          aria-label={b.built ? b.name : `${b.name}, rakennuspaikka`}
          onClick={() => onSelect(b.id)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(b.id))}
        >
          {body}
        </g>
      ),
    });
  }
  for (const d of decorShown) {
    const spot = DECOR[d.id];
    if (spot) items.push({ key: `d-${d.id}`, depth: spot.x + spot.y + 0.5, el: <g aria-label={d.name}>{spot.draw(P(spot.x, spot.y))}</g> });
  }
  items.sort((a, b) => a.depth - b.depth);

  const island = pts(P(0, 0), P(8, 0), P(8, 8), P(0, 8));
  const rim = 12;
  return (
    <svg
      class={`base-scene season-${state.season} ${reducedMotion ? "still" : ""}`}
      viewBox="-265 -45 530 315"
      role="img"
      aria-label={`Retkitukikohta, ${season.label.toLowerCase()}. Rakennettu ${builtIds.size} / ${state.buildings.length}.`}
      style={{ "--roof": season.roof }}
    >
      <rect x="-265" y="-45" width="530" height="315" fill={season.water} />
      {/* Laineet järvessä */}
      {[[-220, 40], [180, 30], [-160, 230], [230, 200], [-40, 270]].map(([wx, wy]) => (
        <path key={wx} d={`M${wx} ${wy} q8 -5 16 0 t16 0`} fill="none" stroke="rgba(255,255,255,.45)" stroke-width="2" stroke-linecap="round" />
      ))}
      {/* Saaren reunat ja pinta */}
      <polygon points={pts(P(0, 8), P(8, 8), P(8, 8, -rim), P(0, 8, -rim))} fill={season.groundSide} />
      <polygon points={pts(P(8, 0), P(8, 8), P(8, 8, -rim), P(8, 0, -rim))} fill={season.groundSide} opacity="0.85" />
      <polygon points={island} fill={season.ground} />
      {/* Polku leiriltä rantaan */}
      <path d={`M${P(4, 3.9).join(" ")} Q${P(5.8, 5.6).join(" ")} ${P(7.4, 6.6).join(" ")}`} fill="none" stroke="rgba(120,90,50,.35)" stroke-width="6" stroke-linecap="round" />
      {items.map((it) => <g key={it.key}>{it.el}</g>)}
    </svg>
  );
}

export { SEASONS };
